import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { CATEGORIES, formatDate, getFallbackImageForArticle } from '../../config';
import { db } from '../../firebase';
import { inferRegionFromText } from '../../utils/inferRegionFromText';
import { uploadPostImageWithThumbnail } from '../../utils/uploadImageToStorage';
import { isFallbackImageUrl } from '../../config';

function normaliseWhitespace(value) {
  return String(value || '').replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function parseThemesInput(value) {
  const raw = String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  // unique
  return raw.filter((t, idx) => raw.indexOf(t) === idx);
}

function AdminNewsManager() {
  const [posts, setPosts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeList, setActiveList] = useState('published'); // 'published' | 'unpublished'

  // Editor state
  const [editingId, setEditingId] = useState('');
  const [draft, setDraft] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [geoFixing, setGeoFixing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPosts(items);
        setLoadingList(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Admin posts list failed', err);
        setPosts([]);
        setLoadingList(false);
      }
    );
    return () => unsub();
  }, []);

  const beginEdit = (post) => {
    setEditingId(post.id);
    setSaveStatus('');
    const isPublished = post.published !== false;
    setDraft({
      title: post.title || '',
      excerpt: post.excerpt || '',
      body: post.body || '',
      imageUrl: post.imageUrl || '',
      imageThumbUrl: post.imageThumbUrl || '',
      imageLqip: post.imageLqip || '',
      category: post.category || 'Market Stats',
      region: post.region || 'Global',
      themes: Array.isArray(post.themes) ? post.themes.join(', ') : '',
      published: isPublished,
      _originalPublished: isPublished,
    });
  };

  const cancelEdit = () => {
    setEditingId('');
    setDraft(null);
    setSaveStatus('');
    setSaving(false);
    setUploadingImage(false);
    setUploadPct(0);
  };

  const uploadImageForDraft = async (file) => {
    if (!editingId || !draft) return;
    if (!file) return;
    const maxBytes = 8 * 1024 * 1024; // 8MB
    if (file.size > maxBytes) {
      setSaveStatus('Image is too large (max 8MB).');
      return;
    }
    if (!String(file.type || '').startsWith('image/')) {
      setSaveStatus('Only image files are supported.');
      return;
    }

    setUploadingImage(true);
    setUploadPct(0);
    setSaveStatus('Uploading image to Firebase Storage…');

    try {
      const uploaded = await uploadPostImageWithThumbnail({
        file,
        folder: `posts/${editingId}`,
        onProgress: setUploadPct,
      });
      setDraft((prev) => ({
        ...prev,
        imageUrl: uploaded.imageUrl,
        imageThumbUrl: uploaded.imageThumbUrl,
        imageLqip: uploaded.imageLqip || '',
      }));
      setSaveStatus('Image uploaded (full + thumbnail). Don’t forget to click Save.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Image upload failed', err);
      setSaveStatus('Image upload failed. Check Storage rules and console.');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId || !draft) return;
    setSaving(true);
    setSaveStatus('Saving…');

    try {
      const nextPublished = !!draft.published;
      const originalPublished = !!draft._originalPublished;
      const nextImageUrl = normaliseWhitespace(draft.imageUrl) || '';

      // Guard: do not allow publishing without a real (non-fallback) image.
      if (nextPublished && (!nextImageUrl || isFallbackImageUrl(nextImageUrl))) {
        setSaveStatus('Cannot publish: upload a real image (not fallback) first.');
        setSaving(false);
        return;
      }

      await updateDoc(doc(db, 'posts', editingId), {
        title: normaliseWhitespace(draft.title),
        excerpt: normaliseWhitespace(draft.excerpt),
        body: normaliseWhitespace(draft.body),
        imageUrl: nextImageUrl || null,
        imageThumbUrl: normaliseWhitespace(draft.imageThumbUrl) || null,
        imageLqip: normaliseWhitespace(draft.imageLqip) || null,
        category: draft.category || 'Market Stats',
        region: normaliseWhitespace(draft.region) || 'Global',
        themes: parseThemesInput(draft.themes),
        published: nextPublished,
        // When publishing a draft, bump publishedAt so it appears in feeds.
        ...(nextPublished && !originalPublished ? { publishedAt: serverTimestamp() } : {}),
        updatedAt: serverTimestamp(),
      });
      setSaveStatus('Saved.');
      setEditingId('');
      setDraft(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Saving post edit failed', err);
      setSaveStatus('Save failed. Check Firestore rules.');
    } finally {
      setSaving(false);
    }
  };

  const fixGeoForPosts = async () => {
    if (!posts.length) return;
    const ok = window.confirm(
      `Auto-fix GEO (region) for the latest ${posts.length} posts?\n\nThis will recalculate region from title/body/sourceUrl and update Firestore.`
    );
    if (!ok) return;

    setGeoFixing(true);
    setSaveStatus('Auto-fixing GEO…');

    let updated = 0;
    let skipped = 0;

    try {
      // Process sequentially to avoid rate limits.
      for (const post of posts) {
        const current = String(post.region || 'Global').trim() || 'Global';
        const text = `${post.title || ''}\n\n${post.body || ''}\n\n${post.excerpt || ''}`;
        const next = inferRegionFromText(text, post.sourceUrl || post.slug || '');

        if (!next || next === current) {
          skipped += 1;
          continue;
        }

        try {
          await updateDoc(doc(db, 'posts', post.id), {
            region: next,
            updatedAt: serverTimestamp(),
          });
          updated += 1;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to update region for post', post.id, err);
        }
      }

      setSaveStatus(`GEO auto-fix done. Updated ${updated}, skipped ${skipped}.`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('GEO auto-fix failed', err);
      setSaveStatus('GEO auto-fix failed. Check console for details.');
    } finally {
      setGeoFixing(false);
    }
  };

  const deletePost = async (post) => {
    const ok = window.confirm(`Delete post "${post.title || post.slug || post.id}"?`);
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'posts', post.id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Delete post failed', err);
      alert('Delete failed. Check Firestore rules.');
    }
  };

  const categoryOptions = useMemo(() => CATEGORIES.filter((c) => c !== 'All'), []);

  const visiblePosts = useMemo(() => {
    const wantPublished = activeList === 'published';
    return posts.filter((p) => (p?.published !== false) === wantPublished);
  }, [activeList, posts]);

  return (
    <div className="admin-tab">
      <section className="content-card" aria-label="Manage posts">
        <div className="content-card-header">
          <div>
            <div className="section-title">News</div>
            <h2 className="section-subtitle">All posts</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <div className="tabs" aria-label="Post visibility tabs">
              <button
                type="button"
                className={`tab-pill ${activeList === 'published' ? 'active' : ''}`}
                onClick={() => setActiveList('published')}
                disabled={loadingList}
              >
                Published
              </button>
              <button
                type="button"
                className={`tab-pill ${activeList === 'unpublished' ? 'active' : ''}`}
                onClick={() => setActiveList('unpublished')}
                disabled={loadingList}
              >
                Not published
              </button>
            </div>
            <div className="secondary-text">{visiblePosts.length} items</div>
            <button
              type="button"
              className="header-outline-button"
              onClick={fixGeoForPosts}
              disabled={geoFixing || loadingList || !posts.length}
              title="Recalculate regions from title/body/source URL and update Firestore"
            >
              {geoFixing ? 'Fixing GEO…' : 'Auto-fix GEO'}
            </button>
          </div>
        </div>

        {loadingList && <p className="secondary-text">Loading posts…</p>}
        {!loadingList && !visiblePosts.length && <p className="secondary-text">No posts found.</p>}

        <div className="admin-list">
          {visiblePosts.map((post) => {
            const isEditing = editingId === post.id;
            const thumb = post.imageUrl || getFallbackImageForArticle(post);
            const needsImage = !post.imageUrl || isFallbackImageUrl(post.imageUrl);

            return (
              <div key={post.id} className="admin-row">
                <div className="admin-row-main">
                  <div className="admin-row-thumb" aria-hidden="true">
                    {thumb ? <img src={thumb} alt="" loading="lazy" /> : null}
                  </div>
                  <div className="admin-row-meta">
                    <div className="admin-row-title">{post.title || post.slug || 'Untitled'}</div>
                    <div className="secondary-text">
                      {post.category || '—'} · {post.region || '—'}
                      {post.publishedAt ? ` · ${formatDate(post.publishedAt)}` : ''}
                      {needsImage ? ' · Needs image' : ''}
                    </div>
                    {post.slug && (
                      <div className="secondary-text">
                        <Link to={`/news/${post.slug}`} className="latest-see-all">
                          Open →
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="admin-row-actions">
                  {!isEditing ? (
                    <>
                      <button
                        type="button"
                        className="header-outline-button"
                        onClick={() => beginEdit(post)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="header-outline-button"
                        onClick={() => deletePost(post)}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="header-outline-button"
                        onClick={cancelEdit}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="cta-button"
                        onClick={saveEdit}
                        disabled={saving}
                      >
                        <span>{saving ? '…' : '✓'}</span> {saving ? 'Saving…' : 'Save'}
                      </button>
                    </>
                  )}
                </div>

                {isEditing && draft && (
                  <div className="admin-editor">
                    <div className="admin-editor-grid">
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Title</label>
                        <input
                          className="text-input"
                          value={draft.title}
                          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        />
                      </div>
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Image URL</label>
                        <input
                          className="text-input"
                          value={draft.imageUrl}
                          onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
                        />
                        <div style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <input
                            className="text-input"
                            type="file"
                            accept="image/*"
                            disabled={saving || uploadingImage}
                            onChange={(e) => {
                              const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                              uploadImageForDraft(f);
                              // allow selecting the same file again later
                              // eslint-disable-next-line no-param-reassign
                              e.target.value = '';
                            }}
                            title="Upload an image to Firebase Storage and set Image URL automatically"
                          />
                          {uploadingImage && (
                            <div className="secondary-text">
                              Uploading… {uploadPct ? `${uploadPct}%` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Image thumbnail URL</label>
                        <input
                          className="text-input"
                          value={draft.imageThumbUrl || ''}
                          onChange={(e) => setDraft({ ...draft, imageThumbUrl: e.target.value })}
                          placeholder="Auto-filled on upload"
                        />
                      </div>
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Image preview (LQIP)</label>
                        <textarea
                          className="text-input"
                          rows={2}
                          value={draft.imageLqip || ''}
                          onChange={(e) => setDraft({ ...draft, imageLqip: e.target.value })}
                          placeholder="Auto-filled on upload (tiny base64 preview)"
                        />
                      </div>
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Published</label>
                        <label className="secondary-text" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={!!draft.published}
                            onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                            disabled={saving}
                          />
                          {draft.published ? 'Visible on site' : 'Not published (hidden)'}
                        </label>
                        {(!draft.imageUrl || isFallbackImageUrl(draft.imageUrl)) && (
                          <div className="secondary-text" style={{ marginTop: 6 }}>
                            This post is using a fallback image. Upload a real image before publishing.
                          </div>
                        )}
                      </div>
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Category</label>
                        <select
                          className="text-input"
                          value={draft.category}
                          onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                        >
                          {categoryOptions.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Region</label>
                        <input
                          className="text-input"
                          value={draft.region}
                          onChange={(e) => setDraft({ ...draft, region: e.target.value })}
                        />
                      </div>
                      <div className="mini-item admin-editor-field admin-editor-wide">
                        <label className="mini-label">Themes (comma-separated)</label>
                        <input
                          className="text-input"
                          value={draft.themes}
                          onChange={(e) => setDraft({ ...draft, themes: e.target.value })}
                        />
                      </div>
                      <div className="mini-item admin-editor-field admin-editor-wide">
                        <label className="mini-label">Excerpt</label>
                        <textarea
                          className="text-input"
                          rows={3}
                          value={draft.excerpt}
                          onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                        />
                      </div>
                      <div className="mini-item admin-editor-field admin-editor-wide">
                        <label className="mini-label">Body</label>
                        <textarea
                          className="text-input"
                          rows={10}
                          value={draft.body}
                          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                        />
                      </div>
                    </div>

                    {saveStatus && (
                      <p className="secondary-text" style={{ marginTop: 10 }}>
                        {saveStatus}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default AdminNewsManager;


